/**
 * Invite Type — controls post-signup routing based on persona.
 * Epic UA-5.1: Persona-Aware Invite & Onboarding Flows
 */

export type InviteType =
  | "platform_user"
  | "buyer_standard"
  | "buyer_hub"
  | "issuer_invited_buyer"
  | "supplier_enrollment"
  | "issuer_staff";

/** Options shown in the InviteUserDialog dropdown (excludes supplier_enrollment which has its own flow) */
export const INVITE_TYPE_OPTIONS: { value: InviteType; label: string }[] = [
  { value: "platform_user", label: "Platform User" },
  { value: "buyer_standard", label: "Buyer (Standard)" },
  { value: "issuer_invited_buyer", label: "Issuer-Invited Buyer" },
  { value: "issuer_staff", label: "Issuer Staff" },
];

/** Display labels for all invite types */
export const INVITE_TYPE_LABELS: Record<InviteType, string> = {
  platform_user: "Platform User",
  buyer_standard: "Buyer (Standard)",
  buyer_hub: "Buyer Hub",
  issuer_invited_buyer: "Issuer-Invited Buyer",
  supplier_enrollment: "Supplier Enrollment",
  issuer_staff: "Issuer Staff",
};

/** Default role and access level auto-suggested when an invite type is selected */
export const INVITE_TYPE_DEFAULTS: Record<
  InviteType,
  { global_role: string; access_level: string }
> = {
  platform_user: { global_role: "user", access_level: "viewer" },
  buyer_standard: { global_role: "user", access_level: "owner" },
  buyer_hub: { global_role: "user", access_level: "owner" },
  issuer_invited_buyer: { global_role: "user", access_level: "owner" },
  supplier_enrollment: { global_role: "user", access_level: "viewer" },
  issuer_staff: { global_role: "manager", access_level: "viewer" },
};

/** Post-signup route mapping per invite type */
export const INVITE_TYPE_ROUTES: Record<InviteType, string> = {
  platform_user: "/action-center",
  buyer_standard: "/onboarding/standard",
  buyer_hub: "/",
  issuer_invited_buyer: "/onboarding/card-program",
  // The public enrollment route requires an invite-specific token and cannot
  // be reconstructed from an authenticated profile.
  supplier_enrollment: "/",
  issuer_staff: "/issuer-hub",
};

/**
 * Invite types whose semantics require a corporate. The invitation-acceptance
 * DB trigger only grants `user_corporate_access` when `corporate_id IS NOT
 * NULL` — a null-corporate invite of one of these types creates an
 * access-less user who still leaks into platform UI. `InviteUserDialog` uses
 * this to require the Entity selector before allowing Send.
 */
export const CORPORATE_SCOPED_INVITE_TYPES: readonly InviteType[] = [
  "buyer_standard",
  "buyer_hub",
  "issuer_invited_buyer",
];
